import { spaceApi } from "@circle-react/api/spaceApi";
import type { QuizAttemptWithCommunityMember } from "@circle-react/components/Spaces/CourseSpace/AdminSummary/Engagement/QuizResults";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { createQueryKeyFactory } from "@circle-react/lib/queries/createQueryKeyFactory";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";
import type { PaginationHelper, Student } from "@circle-react/types";
import type { SpaceResource } from "@circle-react/types/Space";
import { isEmpty } from "lodash";
import {
  keepPreviousData,
  infiniteQueryOptions,
  queryOptions,
} from "react-query";

import type {
  CourseParams,
  CourseId,
  CoursesListResponse,
  CoursesListParams,
  CourseAdminSummary,
  RecentlyEditedCourseItemsResponse,
  CourseSectionsResponse,
  LessonFilesResponse,
  LessonFilesListResponse,
  CourseLessonResponse,
  EditLessonData,
} from "./courses.types";

export const courseKey = createQueryKeyFactory("course");

const COURSES_LIST_DEFAULT_PER_PAGE = 20;

const toId = (id?: CourseId) => Number(id) || 0;

const paramsKey = (params: CourseParams = {}) =>
  JSON.stringify(
    Object.entries(params).sort(([left], [right]) => left.localeCompare(right)),
  );

export const courseQueries = {
  list: ({
    status,
    page = 1,
    perPage = COURSES_LIST_DEFAULT_PER_PAGE,
    query,
    course_type,
    order,
  }: CoursesListParams = {}) =>
    infiniteQueryOptions({
      initialPageParam: page,
      queryKey: courseKey.key(
        "list",
        status ?? "all",
        page,
        perPage,
        query ?? "",
        course_type ?? "",
        JSON.stringify(order ?? {}),
      ),
      queryFn: ({ pageParam = page }: { pageParam?: number }) => {
        const filter = {
          ...(query && { query }),
          ...(course_type && { course_type }),
        };
        return reactQueryGet<CoursesListResponse>(
          internalApi.courses.index({
            params: {
              page: pageParam,
              per_page: perPage,
              ...(status && { status }),
              ...(!isEmpty(filter) && { filter }),
              ...(!isEmpty(order) && { order }),
            },
          }),
        );
      },
      getNextPageParam: lastPage =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
      getPreviousPageParam: firstPage =>
        firstPage.page > 1 ? firstPage.page - 1 : undefined,
      placeholderData: keepPreviousData,
    }),
  adminSummary: (courseId?: CourseId) =>
    queryOptions({
      queryKey: courseKey.key(toId(courseId), "admin-summary"),
      queryFn: () =>
        reactQueryGet<CourseAdminSummary>(
          internalApi.courses.adminSummary({ courseId: toId(courseId) }),
        ),
      enabled: Boolean(courseId),
    }),
  sections: (courseId?: CourseId, params: CourseParams = {}) =>
    queryOptions({
      queryKey: courseKey.key(toId(courseId), "sections", paramsKey(params)),
      queryFn: () =>
        reactQueryGet<CourseSectionsResponse>(
          internalApi.courses.listSections({
            courseId: toId(courseId),
            params,
          }),
        ),
      enabled: Boolean(courseId),
    }),
  lesson: (courseId?: CourseId, lessonId?: CourseId, sectionId?: CourseId) =>
    queryOptions({
      queryKey: courseKey.key(toId(courseId), "lessons", toId(lessonId)),
      queryFn: () =>
        reactQueryGet<CourseLessonResponse>(
          internalApi.courses.getLesson({
            courseId: toId(courseId),
            lessonId: toId(lessonId),
            sectionId: toId(sectionId),
          }),
        ),
      enabled: Boolean(courseId && lessonId && sectionId),
    }),
  editLesson: (courseId?: CourseId, lessonId?: CourseId) =>
    queryOptions({
      queryKey: courseKey.key(
        toId(courseId),
        "lessons",
        toId(lessonId),
        "edit",
      ),
      queryFn: () =>
        reactQueryGet<EditLessonData>(
          internalApi.settings.courses.lessons.show({ id: toId(lessonId) }),
        ),
      enabled: Boolean(courseId && lessonId),
    }),
  lessonFiles: (courseId?: CourseId, lessonId?: CourseId) =>
    queryOptions({
      queryKey: courseKey.key(
        toId(courseId),
        "lessons",
        toId(lessonId),
        "files",
      ),
      queryFn: () =>
        reactQueryGet<LessonFilesResponse>(
          internalApi.courses.lessonFiles.index({
            courseId: toId(courseId),
            lessonId: toId(lessonId),
            params: { per_page: 1 },
          }),
        ),
      enabled: Boolean(courseId && lessonId),
    }),
  lessonEditorFilesList: (courseId?: CourseId, lessonId?: CourseId) =>
    queryOptions({
      queryKey: courseKey.key(
        toId(courseId),
        "lessons",
        toId(lessonId),
        "files",
        "editor",
      ),
      queryFn: () =>
        reactQueryGet<LessonFilesListResponse>(
          internalApi.courses.lessonFiles.index({
            courseId: toId(courseId),
            lessonId: toId(lessonId),
            params: { per_page: 20 },
          }),
        ),
      enabled: Boolean(courseId && lessonId),
    }),
  students: (courseId?: CourseId, params: CourseParams = {}) =>
    queryOptions({
      queryKey: courseKey.key(toId(courseId), "students", paramsKey(params)),
      queryFn: () =>
        reactQueryGet<PaginationHelper<Student>>(
          internalApi.courses.students.index({
            courseId: toId(courseId),
            params,
          }),
        ),
      enabled: Boolean(courseId),
    }),
  quizAttempts: (courseId?: CourseId, params: CourseParams = {}) =>
    queryOptions({
      queryKey: courseKey.key(
        toId(courseId),
        "quiz-attempts",
        paramsKey(params),
      ),
      queryFn: () =>
        reactQueryGet<PaginationHelper<QuizAttemptWithCommunityMember>>(
          internalApi.courses.quizAttempts.index({
            courseId: toId(courseId),
            params,
          }),
        ),
      enabled: Boolean(courseId),
    }),
  show: (courseId?: CourseId) =>
    queryOptions({
      queryKey: courseKey.key(toId(courseId), "show"),
      queryFn: () =>
        spaceApi.show<SpaceResource>({
          id: toId(courseId),
          forReactQuery: true,
        }) as Promise<SpaceResource>,
      enabled: Boolean(courseId),
    }),
  recentlyEdited: () =>
    queryOptions({
      queryKey: courseKey.key("overview", "recently-edited"),
      queryFn: () =>
        reactQueryGet<RecentlyEditedCourseItemsResponse>(
          internalApi.courses.overview(),
        ),
    }),
};

export const courseInvalidations = {
  all: () => queryClient.invalidateQueries({ queryKey: courseKey.root() }),
  course: (courseId: CourseId) =>
    queryClient.invalidateQueries({ queryKey: courseKey.key(toId(courseId)) }),
  lesson: (courseId: CourseId, lessonId: CourseId) =>
    queryClient.invalidateQueries({
      queryKey: courseKey.key(toId(courseId), "lessons", toId(lessonId)),
    }),
  lessonFiles: (courseId: CourseId, lessonId: CourseId) =>
    queryClient.invalidateQueries({
      queryKey: courseKey.key(
        toId(courseId),
        "lessons",
        toId(lessonId),
        "files",
      ),
    }),
  list: () =>
    queryClient.invalidateQueries({ queryKey: courseKey.key("list") }),
};
